import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { forwardRef } from 'react';

import { cn } from '../../lib/styles';

/**
 * Marks the scrollable viewport so DOM helpers (e.g. `useScrollAreaScroll` in `@page/core`)
 * can attach scroll listeners without relying on third-party class names.
 */
export const PAGE_SCROLL_VIEWPORT_ATTR = 'data-page-scroll-viewport' as const;

const ScrollAreaScrollbar = forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Scrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.Scrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-px',
      'z-98',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-[#999999]" />
  </ScrollAreaPrimitive.Scrollbar>
));

export type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  /** Props forwarded to the scroll viewport (e.g. `id`, `aria-*`, extra `className`). */
  viewportProps?: Omit<React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Viewport>, 'children' | 'asChild'>;
};

/**
 * Custom scrollbars and a dedicated scroll viewport.
 * Built on `@radix-ui/react-scroll-area` (same primitive as shadcn/ui).
 */
const ScrollArea = forwardRef<React.ComponentRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  ({ className, children, type = 'hover', viewportProps, ...props }, ref) => {
    const { className: viewportClassName, ...restViewport } = viewportProps ?? {};

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        type={type}
        className={cn('relative overflow-hidden', className)}
        data-slot="scroll-area"
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          data-page-scroll-viewport
          className={cn('h-full w-full rounded-[inherit]', viewportClassName)}
          {...restViewport}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollAreaScrollbar orientation="vertical" />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);

export type ScrollAreaAutosizeProps = ScrollAreaProps;

/**
 * Fills the parent height (`h-full min-h-0`) — use when the parent has a defined height
 * and you want the scroll region to match the pattern.
 */
const ScrollAreaAutosize = forwardRef<React.ComponentRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaAutosizeProps>(
  ({ className, ...props }, ref) => <ScrollArea ref={ref} className={cn('h-full min-h-0', className)} {...props} />,
);

export { ScrollArea, ScrollAreaAutosize, ScrollAreaScrollbar };
